import { useMemo } from 'react'

import { useSession } from '@marketplace-web/shared/session-data'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'
import {
  BundleDiscountsConfigurationDto,
  transformBundleDiscountsConfigurationDto,
  transformUserBundleDiscountDto,
} from '@marketplace-web/bundles/items-bundle-data'

import { BundleDiscountsSettingsState } from '../types'

function isBundleDiscountsConfigurationDto(dto: unknown): dto is BundleDiscountsConfigurationDto {
  return (
    !!dto &&
    typeof dto === 'object' &&
    'default_discounts' in dto &&
    Array.isArray(dto.default_discounts) &&
    'discounts' in dto &&
    Array.isArray(dto.discounts)
  )
}

function useInitialBundleDiscountsSettings() {
  const { user } = useSession()

  const userBundleDiscountDto = user?.bundle_discount
  const bundleDiscountsConfiguration = useSystemConfiguration()?.bundleDiscountsConfiguration

  const userBundleDiscount = useMemo(
    () => userBundleDiscountDto && transformUserBundleDiscountDto(userBundleDiscountDto),
    [userBundleDiscountDto],
  )

  return useMemo<BundleDiscountsSettingsState>(() => {
    if (isBundleDiscountsConfigurationDto(bundleDiscountsConfiguration)) {
      const bundleDiscountsConfigurationModel = transformBundleDiscountsConfigurationDto(
        bundleDiscountsConfiguration,
      )

      return {
        isLoading: false,
        isDiscountEnabled: userBundleDiscount?.isEnabled || false,
        availableDiscountFractions: bundleDiscountsConfigurationModel.discountFractions,
        selectedDiscounts:
          userBundleDiscount?.discounts || bundleDiscountsConfigurationModel.defaultDiscounts,
      }
    }

    return {
      isLoading: true,
      isDiscountEnabled: false,
      availableDiscountFractions: [],
      selectedDiscounts: [],
    }
  }, [userBundleDiscount, bundleDiscountsConfiguration])
}

export default useInitialBundleDiscountsSettings
